import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { caisseAPI } from '../../services/api';

// API générique pour les opérations de caisse
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://plateforme.fhcgroupesarl.com/api';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('fhc_token')}`,
});

const api = {
  get: async (url) => {
    const response = await fetch(`${API_BASE_URL}${url}`, { headers: getHeaders() });
    if (!response.ok) throw new Error('Erreur réseau');
    return response.json();
  },
  post: async (url, data) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Erreur réseau');
    return response.json();
  },
  put: async (url, data) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Erreur réseau');
    return response.json();
  },
  delete: async (url) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error('Erreur réseau');
    return response.json();
  },
};

const ComptabiliteModule = ({ defaultTab = 'dashboard', hideOtherTabs = false, hideHeader = false }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [soldeInitial, setSoldeInitial] = useState(0);
  const [savingSolde, setSavingSolde] = useState(false);
  const [filterMois, setFilterMois] = useState('');
  const [filterRapide, setFilterRapide] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Nouveau : suivre si données déjà chargées
  const [exportingPDF, setExportingPDF] = useState(false); // État pour l'export PDF
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // Pour gérer la modification
  const [sessionCaisse, setSessionCaisse] = useState(null); // Session de caisse actuelle
  const [dernierSoldeFermeture, setDernierSoldeFermeture] = useState(null); // Solde de fermeture de la dernière session
  const [showModalCaisse, setShowModalCaisse] = useState(false); // Modal ouverture/fermeture
  const [modalCaisseType, setModalCaisseType] = useState(''); // 'ouvrir' ou 'fermer'
  const [formCaisse, setFormCaisse] = useState({ solde: 0, notes: '' });
  const [formData, setFormData] = useState({
    type: 'enc',
    date: new Date().toISOString().split('T')[0],
    heure: new Date().toTimeString().split(' ')[0].slice(0, 5), // HH:MM
    piece: '',
    libelle: '',
    beneficiaire: '',
    contact_beneficiaire: '',
    profession_beneficiaire: '',
    montant: 0,
    mode_paiement: 'Espèces',
    notes: ''
  });

  // Charger les opérations depuis le backend (une seule fois)
  useEffect(() => {
    if (activeTab === 'caisse' && !dataLoaded) {
      fetchOperations();
      loadSoldeInitial();
    }
  }, [activeTab, dataLoaded]);

  // Charger la session caisse à chaque fois qu'on arrive sur l'onglet
  useEffect(() => {
    if (activeTab === 'caisse') {
      loadSessionCaisse();
    }
  }, [activeTab]);

  // Debug: Logger sessionCaisse quand elle change
  useEffect(() => {
    console.log('🔄 SessionCaisse state changed:', sessionCaisse);
    if (sessionCaisse) {
      console.log('   - ID:', sessionCaisse.id);
      console.log('   - Statut:', sessionCaisse.statut);
      console.log('   - Date:', sessionCaisse.date);
      
      // Si la caisse est ouverte, activer automatiquement le filtre "Aujourd'hui"
      if (sessionCaisse.statut === 'ouverte') {
        console.log('📅 Activation automatique du filtre "Aujourd\'hui"');
        setFilterRapide('today');
        setDateDebut('');
        setDateFin('');
        setFilterMois('');
      }
    }
  }, [sessionCaisse]);

  const fetchOperations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/operations-caisse');
      if (response.success && response.operations) {
        console.log('DATE EXEMPLE:', response.operations[0]?.date);
        setOperations(response.operations);
        setDataLoaded(true); // Marquer comme chargé
      }
    } catch (error) {
      console.error('Erreur lors du chargement des opérations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSoldeInitial = async () => {
    console.log('🔍 Chargement du solde initial...');
    try {
      // Essayer de charger depuis l'API
      const response = await api.get('/solde-initial-caisse');
      console.log('📡 Réponse API solde initial:', response);
      
      if (response.success && response.montant !== undefined) {
        const montant = parseFloat(response.montant) || 0;
        console.log('✅ Solde initial chargé:', montant);
        setSoldeInitial(montant);
        return;
      } else {
        console.warn('⚠️ Réponse API incomplète:', response);
      }
    } catch (error) {
      console.error('❌ Erreur chargement solde initial depuis API:', error);
    }
    
    // Si l'API échoue, utiliser localStorage comme fallback
    try {
      const storedSolde = localStorage.getItem('fhc_solde_initial_caisse');
      if (storedSolde !== null) {
        const montant = parseFloat(storedSolde) || 0;
        console.log('💾 Solde initial depuis localStorage:', montant);
        setSoldeInitial(montant);
      } else {
        console.log('ℹ️ Aucun solde initial trouvé');
      }
    } catch (error) {
      console.error('❌ Erreur chargement solde initial depuis localStorage:', error);
    }
  };

  const loadSessionCaisse = async () => {
    console.log('🔍 Chargement de la session caisse...');
    try {
      const response = await caisseAPI.getSessionCaisse();
      console.log('📡 Réponse getSessionCaisse:', response);
      
      if (response.success && response.session) {
        console.log('✅ Session caisse trouvée:', response.session);
        console.log('   - Statut:', response.session.statut);
        console.log('   - Solde ouverture:', response.session.solde_ouverture);
        setSessionCaisse(response.session);
      } else {
        console.log('⚠️ Aucune session caisse active');
        setSessionCaisse(null);
      }
      
      // Charger aussi le solde de fermeture de la dernière session fermée
      try {
        const histoResponse = await caisseAPI.historiqueSessionsCaisse({ 
          statut: 'fermee',
          limit: 1 
        });
        
        if (histoResponse.success && histoResponse.sessions && histoResponse.sessions.length > 0) {
          const dernierSolde = parseFloat(histoResponse.sessions[0].solde_fermeture) || 0;
          console.log('💰 Dernier solde de fermeture trouvé:', dernierSolde);
          setDernierSoldeFermeture(dernierSolde);
        } else {
          console.log('⚠️ Aucune session fermée trouvée');
          setDernierSoldeFermeture(null);
        }
      } catch (error) {
        console.error('❌ Erreur chargement dernier solde fermeture:', error);
        setDernierSoldeFermeture(null);
      }
    } catch (error) {
      console.error('❌ Erreur chargement session caisse:', error);
      setSessionCaisse(null);
      setDernierSoldeFermeture(null);
    }
  };

  const handleOuvrirCaisse = async () => {
    setModalCaisseType('ouvrir');
    
    // Utiliser le dernier solde de fermeture qui a déjà été chargé
    const soldeSuggere = dernierSoldeFermeture !== null ? dernierSoldeFermeture : soldeInitial;
    
    console.log('💰 Solde suggéré pour ouverture caisse:', soldeSuggere);
    console.log('   - Dernier solde fermeture:', dernierSoldeFermeture);
    console.log('   - Solde initial système:', soldeInitial);
    
    setFormCaisse({ 
      solde: soldeSuggere || 0, 
      notes: '' 
    });
    
    setShowModalCaisse(true);
  };

  const handleFermerCaisse = () => {
    setModalCaisseType('fermer');
    setFormCaisse({ solde: 0, notes: '' });
    setShowModalCaisse(true);
  };

  const handleSubmitCaisse = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (modalCaisseType === 'ouvrir') {
        const response = await caisseAPI.ouvrirCaisse({
          solde_ouverture: formCaisse.solde,
          notes_ouverture: formCaisse.notes
        });
        
        if (response.success) {
          alert('✅ Caisse ouverte avec succès');
          await loadSessionCaisse();
          setShowModalCaisse(false);
          await fetchOperations();
        }
      } else if (modalCaisseType === 'fermer') {
        const response = await caisseAPI.fermerCaisse({
          notes_fermeture: formCaisse.notes
        });
        
        if (response.success) {
          alert(`✅ Caisse fermée avec succès\n\nSolde de clôture: ${response.session.solde_fermeture} FCFA`);
          await loadSessionCaisse();
          setShowModalCaisse(false);
          await fetchOperations();
        }
      }
    } catch (error) {
      console.error('Erreur gestion caisse:', error);
      alert('❌ ' + (error.message || 'Erreur lors de l\'opération'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSoldeInitial = async () => {
    try {
      setSavingSolde(true);
      const response = await api.post('/solde-initial-caisse', {
        montant: soldeInitial,
      });
      
      if (response.success) {
        alert(response.message || 'Solde initial enregistré avec succès');
      }
    } catch (error) {
      console.error('Erreur sauvegarde solde initial:', error);
      alert('Erreur lors de la sauvegarde du solde initial');
    } finally {
      setSavingSolde(false);
    }
  };

  const genererNumeroPiece = async (type) => {
    try {
      const response = await api.get(`/operations-caisse-numero?type=${type}`);
      if (response.success && response.numero_piece) {
        return response.numero_piece;
      }
    } catch (error) {
      console.error('Erreur génération numéro:', error);
      return type === 'enc' ? 'BCE-001' : 'BCS-001';
    }
  };

  const handleAddOperation = async () => {
    // Vérifier si la caisse est ouverte
    if (!sessionCaisse || sessionCaisse.statut !== 'ouverte') {
      alert('⚠️ La caisse doit être ouverte avant d\'ajouter une opération');
      return;
    }
    
    // Ouvrir le modal immédiatement avec un numéro temporaire
    setFormData({
      type: 'enc',
      date: new Date().toISOString().split('T')[0],
      heure: new Date().toTimeString().split(' ')[0].slice(0, 5), // HH:MM
      piece: '...',
      libelle: '',
      beneficiaire: '',
      contact_beneficiaire: '',
      profession_beneficiaire: '',
      montant: 0,
      mode_paiement: 'Espèces',
      notes: ''
    });
    setShowModal(true);
    
    // Générer le numéro en arrière-plan
    const numeroPiece = await genererNumeroPiece('enc');
    setFormData(prev => ({ ...prev, piece: numeroPiece }));
  };

  const handleSubmitOperation = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      if (editingId) {
        // Modification d'une opération existante
        const response = await api.put(`/operations-caisse/${editingId}`, formData);
        if (response.success) {
          await fetchOperations();
          setShowModal(false);
          setEditingId(null);
          alert('✅ Opération modifiée avec succès');
        }
      } else {
        // Création d'une nouvelle opération
        const response = await api.post('/operations-caisse', formData);
        if (response.success) {
          await fetchOperations();
          setShowModal(false);
          alert('✅ Opération créée avec succès');
        }
      }
      
      // Réinitialiser le formulaire
      setFormData({
        type: 'enc',
        date: new Date().toISOString().split('T')[0],
        piece: '',
        libelle: '',
        beneficiaire: '',
        contact_beneficiaire: '',
        profession_beneficiaire: '',
        montant: 0,
        mode_paiement: 'Espèces',
        notes: ''
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde de l\'opération');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = async (newType) => {
    // Mettre à jour le type immédiatement
    setFormData(prev => ({ ...prev, type: newType, piece: '...' }));
    
    // Générer le nouveau numéro en arrière-plan
    const numeroPiece = await genererNumeroPiece(newType);
    setFormData(prev => ({ ...prev, piece: numeroPiece }));
  };

  // Modifier une opération
  const handleEditOperation = (op) => {
        setFormData({
        type: op.type,
        piece: op.piece,
        libelle: op.libelle,
        montant: parseFloat(op.montant),
        date: op.date.split('T')[0],
        heure: op.heure || '',
        beneficiaire: op.beneficiaire || '',
        contact_beneficiaire: op.contact_beneficiaire || '',
        profession_beneficiaire: op.profession_beneficiaire || '',
        mode_paiement: op.mode_paiement || 'Espèces',
        notes: op.notes || ''
      });
    setEditingId(op.id);
    setShowModal(true);
  };

  // Supprimer une opération
  const handleDeleteOperation = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette opération ?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/operations-caisse/${id}`);
      setOperations(prev => prev.filter(op => op.id !== id));
      setDataLoaded(true);
      alert('✅ Opération supprimée avec succès');
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('❌ Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'export Excel
  const exportToExcel = () => {
    // Préparer les données pour Excel
    const sortedOperations = [...opsFiltrees].sort((a, b) => {
      const da = a.date.slice(0, 10);
      const db = b.date.slice(0, 10);
      if (da !== db) return da < db ? -1 : 1;
      return a.id - b.id;
    });

    let solde = parseFloat(soldeDeReference) || 0;
    const excelData = sortedOperations.map((op, index) => {
      const montant = parseFloat(op.montant) || 0;
      solde = solde + (op.type === 'enc' ? montant : -montant);
      
      const [y, mo, d] = op.date.slice(0, 10).split('-').map(Number);
      const dateFormatted = new Date(y, mo - 1, d).toLocaleDateString('fr-FR');
      
      return {
        '#': index + 1,
        'Date': dateFormatted,
        'Heure': op.heure || '—',
        'N° Pièce': op.piece || '—',
        'Libellé': op.libelle || '',
        'Bénéficiaire': op.beneficiaire || '—',
        'Contact': op.contact_beneficiaire || '—',
        'Profession': op.profession_beneficiaire || '—',
        'Encaissement (+)': op.type === 'enc' ? montant : '',
        'Décaissement (−)': op.type === 'dec' ? montant : '',
        'Solde': solde
      };
    });

    // Ajouter la ligne Total
    excelData.push({
      '#': '',
      'Date': '',
      'Heure': '',
      'N° Pièce': '',
      'Libellé': '',
      'Bénéficiaire': 'TOTAL',
      'Contact': '',
      'Profession': '',
      'Encaissement (+)': totalEnc,
      'Décaissement (−)': totalDec,
      'Solde': ''
    });

    // Ajouter la ligne Solde Final
    excelData.push({
      '#': '',
      'Date': '',
      'Heure': '',
      'N° Pièce': '',
      'Libellé': '',
      'Bénéficiaire': 'SOLDE FINAL',
      'Contact': '',
      'Profession': '',
      'Encaissement (+)': totalEnc,
      'Décaissement (−)': totalDec,
      'Solde': soldeFinal
    });

    // Créer le classeur Excel
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Définir la largeur des colonnes
    const columnWidths = [
      { wch: 5 },  // #
      { wch: 12 }, // Date
      { wch: 8 },  // Heure
      { wch: 12 }, // N° Pièce
      { wch: 30 }, // Libellé
      { wch: 20 }, // Bénéficiaire
      { wch: 15 }, // Contact
      { wch: 15 }, // Profession
      { wch: 15 }, // Encaissement
      { wch: 15 }, // Décaissement
      { wch: 15 }  // Solde
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Journal de Caisse');
    
    // Générer le nom de fichier avec la date
    const today = new Date().toISOString().split('T')[0];
    const filename = `Journal_Caisse_${today}.xlsx`;
    
    // Télécharger le fichier
    XLSX.writeFile(workbook, filename);
  };

  // Fonction d'export PDF
  const exportToPDF = async () => {
    try {
      setExportingPDF(true);
      
      // Préparer les filtres pour l'export
      const filters = {};
      
      // Si un filtre rapide "Aujourd'hui" est actif
      if (filterRapide === 'today') {
        const today = new Date().toISOString().split('T')[0];
        filters.date_debut = today;
        filters.date_fin = today;
      }
      // Sinon, utiliser les dates personnalisées
      else if (dateDebut || dateFin) {
        if (dateDebut) filters.date_debut = dateDebut;
        if (dateFin) filters.date_fin = dateFin;
      }
      // Si un mois est sélectionné et pas de dates personnalisées
      else if (filterMois) {
        const [year, month] = filterMois.split('-');
        const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1);
        const lastDay = new Date(parseInt(year), parseInt(month), 0);
        
        filters.date_debut = firstDay.toISOString().split('T')[0];
        filters.date_fin = lastDay.toISOString().split('T')[0];
      }
      
      // Construire l'URL avec les paramètres
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE_URL}/operations-caisse-export-pdf?${params}`, {
        headers: getHeaders(),
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de l\'exportation PDF');
      }
      
      // Récupérer le blob PDF
      const blob = await response.blob();
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Générer un nom de fichier avec la date actuelle
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `journal-caisse-${dateStr}.pdf`;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Erreur lors de l\'exportation PDF:', error);
      alert('Erreur lors de l\'exportation du PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const getLocalDate = (dateStr) => new Date(new Date(dateStr).getTime() + 60 * 60 * 1000);

  const getMoisKey = (dateStr) => {
    const d = getLocalDate(dateStr);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay() + 1); weekStart.setHours(0,0,0,0);
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);

  const matchesRapide = (op) => {
    const d = getLocalDate(op.date);
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
    if (filterRapide === 'today') return key === todayKey;
    if (dateDebut || dateFin) {
      if (dateDebut && key < dateDebut) return false;
      if (dateFin && key > dateFin) return false;
      return true;
    }
    return true;
  };

  const moisDisponibles = [...new Set(operations.map(o => o.date ? getMoisKey(o.date) : null))].filter(Boolean).sort();
  const opsFiltrees = operations.filter(o => (!filterMois || getMoisKey(o.date) === filterMois) && matchesRapide(o));
  const totalEnc = opsFiltrees.filter(o => o.type === 'enc').reduce((s, o) => s + parseFloat(o.montant || 0), 0);
  const totalDec = opsFiltrees.filter(o => o.type === 'dec').reduce((s, o) => s + parseFloat(o.montant || 0), 0);
  
  // Déterminer le solde de référence :
  // 1. Si session ouverte → utiliser son solde_ouverture
  // 2. Si session fermée et on a le dernier solde de fermeture → utiliser ce solde
  // 3. Sinon → utiliser le solde initial du système
  const soldeDeReference = sessionCaisse?.solde_ouverture 
    ? parseFloat(sessionCaisse.solde_ouverture) 
    : (dernierSoldeFermeture !== null ? dernierSoldeFermeture : soldeInitial);
  const soldeFinal = soldeDeReference + totalEnc - totalDec;

  const fmtDate = (dateStr) => {
    const moisNoms = ['jan','fév','mars','avr','mai','juin','juil','août','sep','oct','nov','déc'];
    const d = new Date(new Date(dateStr).getTime() + 60 * 60 * 1000);
    return `${d.getUTCDate()} ${moisNoms[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  };

  const fmtN = n => Math.round(n).toLocaleString('fr-FR');
  const fmtExact = (n) => {
    if (!n && n !== 0) return '0,00';
    const num = parseFloat(n);
    if (Number.isInteger(num)) return num.toLocaleString('fr-FR');
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtK = n => n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(0) + 'k' : String(n);

  // Fonction pour obtenir le début et la fin de la semaine actuelle
  const getWeekRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday);
    monday.setHours(0, 0, 0, 0);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return { 
      debut: monday.toISOString().split('T')[0], 
      fin: sunday.toISOString().split('T')[0] 
    };
  };

  return (
    <div style={{ padding: '20px 24px', background: '#080e1a', minHeight: '100vh' }}>
      {/* Header */}
      {!hideHeader && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 24, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
            💰 FHC — Comptabilité · SYSCOHADA
          </h1>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)' }}>
            MCS BTP · Exercice clos le 31/12/2023 · NIF 0202011572442
          </p>
        </div>
        <button style={{
          background: 'rgba(201,168,76,0.12)',
          border: '1px solid rgba(201,168,76,0.3)',
          borderRadius: 7,
          padding: '6px 14px',
          color: '#C9A84C',
          fontSize: 11,
          cursor: 'pointer'
        }}>
          🖨️ Imprimer
        </button>
      </div>
      )}

      {/* Tabs */}
      {!hideOtherTabs && (
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 20, overflowX: 'auto' }}>
        {[
          { id: 'dashboard', label: '📊 Tableau de bord' },
          { id: 'bilan', label: '🏛️ Bilan' },
          { id: 'resultat', label: '📈 Compte de résultat' },
          { id: 'sig', label: '⚖️ Soldes intermédiaires' },
          { id: 'flux', label: '💧 Flux de trésorerie' },
          { id: 'notes', label: '📋 Notes annexées' },
          { id: 'journal', label: '📒 Journal général' },
          { id: 'identite', label: '🏢 Fiche entité' },
          { id: 'caisse', label: '💵 Journal de Caisse' },
          { id: 'bons', label: '📄 Bons de Caisse' },
          { id: 'billitage', label: '🪙 Cahier de Billitage' }
        ].map(tab => (
          <div
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '11px 16px',
              fontSize: 12,
              color: activeTab === tab.id ? '#C9A84C' : '#667',
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '2px solid #C9A84C' : '2px solid transparent',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div>
          <div style={{
            background: 'rgba(251,140,0,0.07)',
            border: '1px solid rgba(251,140,0,0.2)',
            borderRadius: 9,
            padding: '12px 16px',
            marginBottom: 14,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start'
          }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#ffb74d' }}>Chiffre d'affaires en forte baisse</div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>
                Le CA 2023 (962 834 FCFA) représente seulement 44% du CA 2022 (2 202 400 FCFA). Recul de 56% en un an. 
                Cependant, l'entreprise retrouve la rentabilité avec un résultat net positif de 108 959 FCFA après une perte de 30 059 FCFA en 2022.
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 }}>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#ef8080' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>💼</div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2 }}>962 834</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>CA HT 2023 (FCFA)</div>
              <div style={{ fontSize: 9, marginTop: 5, padding: '2px 6px', borderRadius: 99, display: 'inline-block', background: 'rgba(229,57,53,0.15)', color: '#ef8080' }}>
                ↓ −56% vs 2022
              </div>
            </div>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#66bb6a' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>✅</div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2, color: '#66bb6a' }}>+108 959</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>Résultat net 2023</div>
              <div style={{ fontSize: 9, marginTop: 5, padding: '2px 6px', borderRadius: 99, display: 'inline-block', background: 'rgba(67,160,71,0.15)', color: '#66bb6a' }}>
                ↑ Bénéfice (vs perte N-1)
              </div>
            </div>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#C9A84C' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>🏛️</div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2 }}>1 356 098</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>Capitaux propres</div>
              <div style={{ fontSize: 9, marginTop: 5, padding: '2px 6px', borderRadius: 99, display: 'inline-block', background: 'rgba(67,160,71,0.15)', color: '#66bb6a' }}>
                ↑ +9% vs 2022
              </div>
            </div>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#ef8080' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>🏦</div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2, color: '#ef8080' }}>−325 081</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>Trésorerie nette</div>
              <div style={{ fontSize: 9, marginTop: 5, padding: '2px 6px', borderRadius: 99, display: 'inline-block', background: 'rgba(229,57,53,0.15)', color: '#ef8080' }}>
                Découvert bancaire
              </div>
            </div>
          </div>

          <div style={{
            background: 'rgba(21,101,192,0.07)',
            border: '1px solid rgba(21,101,192,0.2)',
            borderRadius: 9,
            padding: '12px 16px',
            marginBottom: 14,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start'
          }}>
            <span style={{ fontSize: 20 }}>ℹ️</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64b5f6' }}>Contexte et interprétation</div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 3, lineHeight: 1.6 }}>
                MCS BTP est une entreprise de BTP génie civil en phase de redressement. Malgré une forte contraction du CA (-56%), 
                elle renoue avec le bénéfice grâce à une maîtrise des charges. La valeur ajoutée de 487 834 FCFA (51% du CA) est satisfaisante. 
                Le risque principal est la trésorerie négative (-325 081 FCFA) due à un découvert bancaire de 546 330 FCFA non couvert par les 221 249 FCFA en caisse.
                Les dettes fiscales/sociales s'élèvent à 209 820 FCFA (TVA + organismes sociaux) à régulariser.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Journal de Caisse Tab */}
      {activeTab === 'caisse' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
            <div>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
                💵 Journal de Caisse
              </h2>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)' }}>
                Encaissements · Décaissements · Soldes
                {loading && dataLoaded && (
                  <span style={{ marginLeft: 10, color: '#C9A84C' }}>
                    <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span> Actualisation...
                  </span>
                )}
              </p>
              {/* Affichage de l'état de la session */}
              {sessionCaisse && sessionCaisse.statut === 'ouverte' && (
                <div style={{ 
                  marginTop: 8, 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 8,
                  background: 'rgba(76,175,80,0.12)',
                  border: '1px solid rgba(76,175,80,0.3)',
                  borderRadius: 6,
                  padding: '4px 10px'
                }}>
                  <span style={{ fontSize: 10, color: '#66bb6a', fontWeight: 600 }}>
                    🔓 Caisse ouverte
                  </span>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>|</span>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)' }}>
                    {sessionCaisse.heure_ouverture ? sessionCaisse.heure_ouverture.slice(0, 5) : '—'}
                  </span>
                  {sessionCaisse.ouvert_par_nom && (
                    <>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>|</span>
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)' }}>
                        par {sessionCaisse.ouvert_par_nom}
                      </span>
                    </>
                  )}
                </div>
              )}
              {(!sessionCaisse || sessionCaisse.statut === 'fermee') && (
                <div style={{ 
                  marginTop: 8, 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: 8,
                  background: 'rgba(229,57,53,0.12)',
                  border: '1px solid rgba(229,57,53,0.3)',
                  borderRadius: 6,
                  padding: '4px 10px'
                }}>
                  <span style={{ fontSize: 10, color: '#ef8080', fontWeight: 600 }}>
                    🔒 Caisse fermée
                  </span>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)' }}>
                    Veuillez ouvrir la caisse pour ajouter des opérations
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Bouton Ouvrir/Fermer la caisse */}
              {(!sessionCaisse || sessionCaisse.statut === 'fermee') && (
                <button 
                  onClick={handleOuvrirCaisse}
                  style={{
                    background: 'linear-gradient(135deg,#66bb6a,#43a047)',
                    border: 'none',
                    borderRadius: 7,
                    padding: '8px 16px',
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  title="Ouvrir la caisse"
                >
                  🔓 Ouvrir la caisse
                </button>
              )}
              {sessionCaisse && sessionCaisse.statut === 'ouverte' && (
                <button 
                  onClick={handleFermerCaisse}
                  style={{
                    background: 'linear-gradient(135deg,#ef5350,#c62828)',
                    border: 'none',
                    borderRadius: 7,
                    padding: '8px 16px',
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                  title="Arrêter la caisse"
                >
                  🔒 Arrêter la caisse
                </button>
              )}
              <button 
                onClick={exportToPDF}
                disabled={exportingPDF}
                style={{
                  background: exportingPDF ? 'rgba(239,83,80,0.3)' : 'linear-gradient(135deg,#ef5350,#c62828)',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 16px',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: exportingPDF ? 'not-allowed' : 'pointer',
                  opacity: exportingPDF ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
                title="Exporter en PDF"
              >
                {exportingPDF ? '⏳ Export...' : '📄 Export PDF'}
              </button>
              <button onClick={exportToExcel} style={{
                background: 'rgba(76,175,80,0.12)',
                border: '1px solid rgba(76,175,80,0.3)',
                borderRadius: 7,
                padding: '8px 16px',
                color: '#66bb6a',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}>
                📊 Export Excel
              </button>
              <button 
                onClick={handleAddOperation} 
                disabled={!sessionCaisse || sessionCaisse.statut !== 'ouverte'}
                style={{
                  background: (!sessionCaisse || sessionCaisse.statut !== 'ouverte') 
                    ? 'rgba(201,168,76,0.3)' 
                    : 'linear-gradient(135deg,#C9A84C,#8a6028)',
                  border: 'none',
                  borderRadius: 7,
                  padding: '8px 16px',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: (!sessionCaisse || sessionCaisse.statut !== 'ouverte') ? 'not-allowed' : 'pointer',
                  opacity: (!sessionCaisse || sessionCaisse.statut !== 'ouverte') ? 0.5 : 1
                }}
                title={(!sessionCaisse || sessionCaisse.statut !== 'ouverte') ? 'Ouvrez d\'abord la caisse' : 'Ajouter une opération'}
              >
                + Opération
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 }}>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#66bb6a' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>💰</div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2, color: soldeFinal >= 0 ? '#66bb6a' : '#ef8080' }}>
                {fmtExact(soldeFinal)} F
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>Solde final</div>
            </div>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#42a5f5' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>📥</div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2, color: '#42a5f5' }}>
                {fmtExact(totalEnc)} F
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>Total encaissements</div>
            </div>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#ef5350' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>📤</div>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2, color: '#ef5350' }}>
                {fmtExact(totalDec)} F
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>Total décaissements</div>
            </div>
            <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '13px 15px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#C9A84C' }} />
              <div style={{ fontSize: 18, marginBottom: 6 }}>🏦</div>
              {sessionCaisse && sessionCaisse.statut === 'ouverte' ? (
                // Si la caisse est ouverte, afficher le solde d'ouverture de la session (lecture seule)
                <>
                  <div style={{ fontSize: 11, color: '#667', marginBottom: 4 }}>Solde d'ouverture :</div>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2, color: '#C9A84C' }}>
                    {fmtExact(sessionCaisse.solde_ouverture || 0)} F
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>Session en cours</div>
                </>
              ) : (
                // Si la caisse est fermée, afficher le solde de référence (dernier solde de fermeture ou solde initial)
                <>
                  <div style={{ fontSize: 11, color: '#667', marginBottom: 4 }}>
                    {dernierSoldeFermeture !== null ? 'Dernier solde de clôture :' : 'Solde initial :'}
                  </div>
                  <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, lineHeight: 1, marginBottom: 2, color: '#C9A84C' }}>
                    {fmtExact(soldeDeReference)} F
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.42)' }}>
                    {dernierSoldeFermeture !== null ? 'Session précédente fermée' : 'Aucune session précédente'}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Filtres */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button onClick={() => { setFilterRapide(filterRapide === 'today' ? '' : 'today'); setDateDebut(''); setDateFin(''); setFilterMois(''); }}
              style={{ padding: '5px 13px', borderRadius: 20, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                background: filterRapide === 'today' ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)',
                border: filterRapide === 'today' ? '1px solid #C9A84C' : '1px solid rgba(255,255,255,0.1)',
                color: filterRapide === 'today' ? '#C9A84C' : '#889' }}>
              Aujourd'hui
            </button>
            <button onClick={() => { 
              const { debut, fin } = getWeekRange();
              setFilterRapide('week');
              setDateDebut(debut);
              setDateFin(fin);
              setFilterMois('');
            }}
              style={{ padding: '5px 13px', borderRadius: 20, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                background: filterRapide === 'week' ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)',
                border: filterRapide === 'week' ? '1px solid #C9A84C' : '1px solid rgba(255,255,255,0.1)',
                color: filterRapide === 'week' ? '#C9A84C' : '#889' }}>
              Cette semaine
            </button>
            <span style={{ fontSize: 11, color: '#556' }}>|</span>
            <span style={{ fontSize: 11, color: '#667' }}>Periode :</span>
            <input type="date" value={dateDebut} onChange={e => { setDateDebut(e.target.value); setFilterRapide(''); setFilterMois(''); }}
              style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '4px 10px', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }} />
            <span style={{ fontSize: 11, color: '#667' }}>au</span>
            <input type="date" value={dateFin} onChange={e => { setDateFin(e.target.value); setFilterRapide(''); setFilterMois(''); }}
              style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '4px 10px', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }} />
            <span style={{ fontSize: 11, color: '#556' }}>|</span>
            <select value={filterMois} onChange={e => { setFilterMois(e.target.value); setFilterRapide(''); setDateDebut(''); setDateFin(''); }}
              style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '5px 12px', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
              <option value=''>Tous les mois</option>
              {moisDisponibles.map(m => (
                <option key={m} value={m}>
                  {new Date(parseInt(m.slice(0,4)), parseInt(m.slice(5,7))-1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </option>
              ))}
            </select>
            {(filterMois || filterRapide || dateDebut || dateFin) && (
              <button onClick={() => { setFilterMois(''); setFilterRapide(''); setDateDebut(''); setDateFin(''); }}
                style={{ background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 6, padding: '4px 10px', color: '#889', fontSize: 11, cursor: 'pointer' }}>
                x Tout afficher
              </button>
            )}
          </div>

          <div style={{ background: '#111f2e', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 11, padding: 0, overflow: 'hidden' }}>
            {loading && !dataLoaded && (
              <div style={{ padding: 20, textAlign: 'center', color: '#C9A84C' }}>
                <div style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</div> Chargement...
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            {!loading && operations.length === 0 && dataLoaded && (
              <div style={{ padding: 40, textAlign: 'center', color: '#667' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>📝</div>
                <div style={{ fontSize: 14, marginBottom: 5 }}>Aucune opération enregistrée</div>
                <div style={{ fontSize: 11 }}>Cliquez sur "+ Opération" pour ajouter une opération de caisse</div>
              </div>
            )}
            {(dataLoaded || operations.length > 0) && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>#</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>Date</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>Heure</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>N° Pièce</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>Libellé</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>Bénéficiaire</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>Contact</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'left', background: '#172638', whiteSpace: 'nowrap' }}>Profession</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'right', background: '#172638', whiteSpace: 'nowrap' }}>Encaiss. (+)</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'right', background: '#172638', whiteSpace: 'nowrap' }}>Décaiss. (−)</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'right', background: '#172638', whiteSpace: 'nowrap' }}>Solde</th>
                  <th style={{ fontSize: 9, color: '#C9A84C', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', padding: '9px 10px', borderBottom: '2px solid rgba(201,168,76,0.3)', textAlign: 'center', background: '#172638', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let solde = parseFloat(soldeDeReference) || 0;
                  
                  const sortedOperations = [...opsFiltrees].sort((a, b) => {
                    const da = a.date.slice(0, 10);
                    const db = b.date.slice(0, 10);
                    if (da !== db) return da < db ? -1 : 1;
                    return a.id - b.id;
                  });
                  
                  return sortedOperations.map((op, i) => {
                    const montant = parseFloat(op.montant) || 0;
                    solde = solde + (op.type === 'enc' ? montant : -montant);
                    
                    const dateFormatted = fmtDate(op.date);
                    
                    return (
                      <tr 
                        key={op.id} 
                        style={{ 
                          background: i % 2 === 0 
                            ? (op.type === 'enc' ? 'rgba(67,160,71,.08)' : 'rgba(229,57,53,.08)')
                            : (op.type === 'enc' ? 'rgba(67,160,71,.04)' : 'rgba(229,57,53,.04)'),
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = op.type === 'enc' ? 'rgba(67,160,71,.15)' : 'rgba(229,57,53,.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = i % 2 === 0 
                            ? (op.type === 'enc' ? 'rgba(67,160,71,.08)' : 'rgba(229,57,53,.08)')
                            : (op.type === 'enc' ? 'rgba(67,160,71,.04)' : 'rgba(229,57,53,.04)');
                        }}
                      >
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', color: '#667', fontSize: 10, fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 10, color: '#bbb', whiteSpace: 'nowrap' }}>
                          {dateFormatted}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 10, color: '#C9A84C', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {op.heure ? op.heure.slice(0, 5) : '—'}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                          <span style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: 5,
                            background: op.type === 'enc' ? 'rgba(67,160,71,.20)' : 'rgba(229,57,53,.20)',
                            color: op.type === 'enc' ? '#66bb6a' : '#ef8080'
                          }}>
                            {op.piece}
                          </span>
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 11, fontWeight: 500, color: '#fff' }}>
                          {op.libelle}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 10, color: '#aaa' }}>
                          {op.beneficiaire}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 10, color: '#889' }}>
                          {op.contact_beneficiaire || '—'}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', fontSize: 10, color: '#889' }}>
                          {op.profession_beneficiaire || '—'}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', textAlign: 'right', color: '#66bb6a', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {op.type === 'enc' ? `${fmtExact(montant)} F` : '—'}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', textAlign: 'right', color: '#ef8080', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {op.type === 'dec' ? `${fmtExact(montant)} F` : '—'}
                        </td>
                        <td style={{ padding: '11px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', textAlign: 'right', fontWeight: 700, fontSize: 12, color: solde >= 0 ? '#C9A84C' : '#ef8080', whiteSpace: 'nowrap' }}>
                          {fmtExact(solde)} F
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,.08)', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => handleEditOperation(op)}
                            title="Modifier"
                            style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 5, padding: '4px 8px', color: '#C9A84C', fontSize: 11, cursor: 'pointer', marginRight: 4 }}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteOperation(op.id)}
                            title="Supprimer"
                            style={{ background: 'rgba(229,57,53,0.12)', border: '1px solid rgba(229,57,53,0.3)', borderRadius: 5, padding: '4px 8px', color: '#ef8080', fontSize: 11, cursor: 'pointer' }}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  });
                })()}
                {/* Ligne TOTAL */}
                <tr style={{ background: 'rgba(201,168,76,.06)', borderTop: '1px solid rgba(201,168,76,.25)' }}>
                  <td colSpan={8} style={{ fontWeight: 600, color: '#aaa', padding: '10px 10px', fontSize: 11, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                    Total
                  </td>
                  <td style={{ textAlign: 'right', color: '#66bb6a', fontWeight: 600, padding: '10px 10px', whiteSpace: 'nowrap' }}>
                    {fmtExact(totalEnc)} F
                  </td>
                  <td style={{ textAlign: 'right', color: '#ef8080', fontWeight: 600, padding: '10px 10px', whiteSpace: 'nowrap' }}>
                    {fmtExact(totalDec)} F
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 13, color: '#aaa', padding: '10px 10px', whiteSpace: 'nowrap' }}>—</td>
                  <td />
                </tr>
                {/* Ligne SOLDE FINAL */}
                <tr style={{ background: 'rgba(201,168,76,.10)', borderTop: '2px solid rgba(201,168,76,.4)' }}>
                  <td colSpan={8} style={{ fontWeight: 700, color: '#C9A84C', padding: '12px 10px', fontSize: 12, letterSpacing: '0.5px' }}>
                    SOLDE FINAL
                  </td>
                  <td style={{ textAlign: 'right', color: '#66bb6a', fontWeight: 700, padding: '12px 10px', whiteSpace: 'nowrap' }}>
                    {fmtExact(totalEnc)} F
                  </td>
                  <td style={{ textAlign: 'right', color: '#ef8080', fontWeight: 700, padding: '12px 10px', whiteSpace: 'nowrap' }}>
                    {fmtExact(totalDec)} F
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 14, color: soldeFinal >= 0 ? '#C9A84C' : '#ef8080', padding: '12px 10px', whiteSpace: 'nowrap' }}>
                    {fmtExact(soldeFinal)} F
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
            )}
          </div>
        </div>
      )}

      {/* Modal Formulaire Opération */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: '#111f2e',
            borderRadius: 12,
            padding: 24,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid rgba(201,168,76,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 600, color: '#C9A84C', margin: 0 }}>
                {editingId ? "✏️ Modifier l'opération" : '+ Nouvelle opération'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#888',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 0,
                  width: 30,
                  height: 30
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmitOperation}>
              <div style={{ display: 'grid', gap: 16 }}>
                {/* Type */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 8, fontWeight: 600 }}>
                    Type d'opération *
                  </label>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8, 
                      cursor: 'pointer',
                      padding: '10px 16px',
                      background: formData.type === 'enc' ? 'rgba(67,160,71,.15)' : 'rgba(255,255,255,0.05)',
                      border: formData.type === 'enc' ? '2px solid #66bb6a' : '2px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      transition: 'all 0.2s',
                      flex: 1
                    }}>
                      <input
                        type="radio"
                        name="type"
                        value="enc"
                        checked={formData.type === 'enc'}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        required
                        style={{ cursor: 'pointer', width: 16, height: 16 }}
                      />
                      <span style={{ fontSize: 12, color: formData.type === 'enc' ? '#66bb6a' : '#aaa', fontWeight: 600 }}>
                        📥 Encaissement (+)
                      </span>
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8, 
                      cursor: 'pointer',
                      padding: '10px 16px',
                      background: formData.type === 'dec' ? 'rgba(229,57,53,.15)' : 'rgba(255,255,255,0.05)',
                      border: formData.type === 'dec' ? '2px solid #ef8080' : '2px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      transition: 'all 0.2s',
                      flex: 1
                    }}>
                      <input
                        type="radio"
                        name="type"
                        value="dec"
                        checked={formData.type === 'dec'}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        required
                        style={{ cursor: 'pointer', width: 16, height: 16 }}
                      />
                      <span style={{ fontSize: 12, color: formData.type === 'dec' ? '#ef8080' : '#aaa', fontWeight: 600 }}>
                        📤 Décaissement (-)
                      </span>
                    </label>
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 12,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Heure */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                    Heure *
                  </label>
                  <input
                    type="time"
                    value={formData.heure || ''}
                    onChange={(e) => setFormData({ ...formData, heure: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 12,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Numéro de pièce (automatique, lecture seule) */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                    N° Pièce (automatique)
                  </label>
                  <input
                    type="text"
                    value={formData.piece}
                    readOnly
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: formData.piece === '...' ? '#888' : '#C9A84C',
                      fontSize: 12,
                      outline: 'none',
                      cursor: 'not-allowed',
                      fontWeight: formData.piece === '...' ? 'normal' : 600
                    }}
                  />
                  <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                    {formData.piece === '...' ? '⏳ Génération en cours...' : '✅ Le numéro est généré automatiquement par le système'}
                  </div>
                </div>

                {/* Libellé */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                    Libellé *
                  </label>
                  <input
                    type="text"
                    value={formData.libelle}
                    onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                    required
                    maxLength={255}
                    placeholder="Ex: Règlement facture, Achat matériel..."
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 12,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Bénéficiaire */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                    Bénéficiaire *
                  </label>
                  <input
                    type="text"
                    value={formData.beneficiaire}
                    onChange={(e) => setFormData({ ...formData, beneficiaire: e.target.value })}
                    required
                    maxLength={255}
                    placeholder="Nom du bénéficiaire"
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 12,
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Contact et Profession du bénéficiaire */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                      Contact bénéficiaire
                    </label>
                    <input
                      type="text"
                      value={formData.contact_beneficiaire}
                      onChange={(e) => setFormData({ ...formData, contact_beneficiaire: e.target.value })}
                      maxLength={255}
                      placeholder="Téléphone ou email"
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.07)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        padding: '8px 12px',
                        color: '#fff',
                        fontSize: 12,
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                      Profession bénéficiaire
                    </label>
                    <input
                      type="text"
                      value={formData.profession_beneficiaire}
                      onChange={(e) => setFormData({ ...formData, profession_beneficiaire: e.target.value })}
                      maxLength={255}
                      placeholder="Profession ou activité"
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.07)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        padding: '8px 12px',
                        color: '#fff',
                        fontSize: 12,
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                {/* Montant et Mode de paiement */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                      Montant (FCFA) *
                    </label>
                    <input
                      type="number"
                      value={formData.montant === 0 ? '' : formData.montant}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, montant: val === '' ? 0 : parseInt(val) || 0 });
                      }}
                      onFocus={(e) => {
                        if (e.target.value === '0') {
                          e.target.value = '';
                        }
                      }}
                      required
                      min="0"
                      step="1"
                      placeholder="0"
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.07)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        padding: '8px 12px',
                        color: '#fff',
                        fontSize: 12,
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                      Mode de paiement *
                    </label>
                    <select
                      value={formData.mode_paiement}
                      onChange={(e) => setFormData({ ...formData, mode_paiement: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.07)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        padding: '8px 12px',
                        color: '#fff',
                        fontSize: 12,
                        outline: 'none'
                      }}
                    >
                      <option value="Espèces">Espèces</option>
                      <option value="Chèque">Chèque</option>
                      <option value="Virement">Virement</option>
                      <option value="Mobile Money">Mobile Money</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#C9A84C', marginBottom: 6, fontWeight: 600 }}>
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Notes complémentaires..."
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 12,
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* Boutons */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 7,
                      padding: '8px 20px',
                      color: '#999',
                      fontSize: 12,
                      cursor: 'pointer'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      background: loading ? '#555' : 'linear-gradient(135deg,#C9A84C,#8a6028)',
                      border: 'none',
                      borderRadius: 7,
                      padding: '8px 20px',
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gestion Caisse (Ouverture/Fermeture) */}
      {showModalCaisse && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            background: '#111f2e',
            borderRadius: 12,
            padding: 24,
            maxWidth: 500,
            width: '100%',
            border: modalCaisseType === 'ouvrir' 
              ? '1px solid rgba(76,175,80,0.3)' 
              : '1px solid rgba(229,57,53,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ 
                fontFamily: 'Cormorant Garamond, serif', 
                fontSize: 22, 
                fontWeight: 600, 
                color: modalCaisseType === 'ouvrir' ? '#66bb6a' : '#ef8080', 
                margin: 0 
              }}>
                {modalCaisseType === 'ouvrir' ? '🔓 Ouverture de caisse' : '🔒 Arrêt de caisse'}
              </h2>
              <button
                onClick={() => setShowModalCaisse(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#888',
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 0,
                  width: 30,
                  height: 30
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmitCaisse}>
              <div style={{ display: 'grid', gap: 16 }}>
                {modalCaisseType === 'ouvrir' && (
                  <>
                    <div style={{
                      background: 'rgba(76,175,80,0.08)',
                      border: '1px solid rgba(76,175,80,0.2)',
                      borderRadius: 9,
                      padding: '12px 16px',
                      marginBottom: 4
                    }}>
                      <div style={{ fontSize: 11, color: '#66bb6a', lineHeight: 1.6 }}>
                        Vous êtes sur le point d'ouvrir la caisse pour la journée. Veuillez indiquer le solde d'ouverture.
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: '#66bb6a', marginBottom: 6, fontWeight: 600 }}>
                        Solde d'ouverture (FCFA) *
                      </label>
                      <input
                        type="number"
                        value={formCaisse.solde === 0 ? '' : formCaisse.solde}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormCaisse({ ...formCaisse, solde: val === '' ? 0 : parseFloat(val) || 0 });
                        }}
                        required
                        min="0"
                        step="0.01"
                        placeholder="0"
                        style={{
                          width: '100%',
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 6,
                          padding: '10px 12px',
                          color: '#66bb6a',
                          fontSize: 16,
                          fontWeight: 600,
                          outline: 'none'
                        }}
                      />
                    </div>
                  </>
                )}

                {modalCaisseType === 'fermer' && (
                  <>
                    <div style={{
                      background: 'rgba(229,57,53,0.08)',
                      border: '1px solid rgba(229,57,53,0.2)',
                      borderRadius: 9,
                      padding: '12px 16px',
                      marginBottom: 4
                    }}>
                      <div style={{ fontSize: 11, color: '#ef8080', lineHeight: 1.6, marginBottom: 8 }}>
                        Vous êtes sur le point de fermer la caisse. Le système calculera automatiquement le solde de clôture.
                      </div>
                      {sessionCaisse && (
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
                          <div>💰 Solde d'ouverture: <strong>{fmtExact(sessionCaisse.solde_ouverture || 0)} FCFA</strong></div>
                          <div style={{ marginTop: 4 }}>📥 Total encaissements: <strong style={{ color: '#66bb6a' }}>{fmtExact(totalEnc)} FCFA</strong></div>
                          <div style={{ marginTop: 4 }}>📤 Total décaissements: <strong style={{ color: '#ef8080' }}>{fmtExact(totalDec)} FCFA</strong></div>
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            💵 Solde de clôture estimé: <strong style={{ color: '#C9A84C', fontSize: 13 }}>{fmtExact(soldeFinal)} FCFA</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: 11, 
                    color: modalCaisseType === 'ouvrir' ? '#66bb6a' : '#ef8080', 
                    marginBottom: 6, 
                    fontWeight: 600 
                  }}>
                    Notes {modalCaisseType === 'ouvrir' ? '(facultatif)' : ''}
                  </label>
                  <textarea
                    value={formCaisse.notes}
                    onChange={(e) => setFormCaisse({ ...formCaisse, notes: e.target.value })}
                    rows={3}
                    placeholder={modalCaisseType === 'ouvrir' 
                      ? 'Notes d\'ouverture...' 
                      : 'Notes de clôture, observations...'}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      padding: '8px 12px',
                      color: '#fff',
                      fontSize: 12,
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* Boutons */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => setShowModalCaisse(false)}
                    style={{
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 7,
                      padding: '8px 20px',
                      color: '#999',
                      fontSize: 12,
                      cursor: 'pointer'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      background: loading ? '#555' : (modalCaisseType === 'ouvrir' 
                        ? 'linear-gradient(135deg,#66bb6a,#43a047)' 
                        : 'linear-gradient(135deg,#ef5350,#c62828)'),
                      border: 'none',
                      borderRadius: 7,
                      padding: '8px 20px',
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Traitement...' : (modalCaisseType === 'ouvrir' ? '🔓 Ouvrir' : '🔒 Arrêter')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComptabiliteModule;
